import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, useSearchParams } from 'react-router-dom';
import {
  useGetMeetingsRegisterQuery,
  useLazyExportMeetingsRegisterDocxQuery,
  useListMeetingDepartmentsQuery,
  useListPtoProjectsQuery,
  useMeQuery,
  useSetMeetingAssignmentStatusMutation,
} from '@shared/api';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import { useAgentPermission } from '@entities/user';
import {
  assignmentStatusLabel,
  assignmentStatusTone,
  fmtDueDate,
  fmtMeetingDate,
  targetAgentLabel,
} from '@entities/meetings';
import type { RegisterAssignment } from '@entities/meetings/types';
import { Card } from '@shared/ui/Card';
import { FallbackBanner } from '@shared/ui/FallbackBanner';

function normName(s?: string | null): string {
  return (s ?? '').trim().toLowerCase().replace(/ё/g, 'е');
}

/** Скачать Blob как файл (клиентский триггер: object URL → клик по ссылке). */
function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const Intro = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(5)};
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 720px;
`;

const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

const FilterLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FilterSelect = styled.select`
  font-size: 13px;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const QuickToggleBtn = styled.button<{ $active?: boolean }>`
  font-size: 12px;
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) => ($active ? theme.colors.primarySoft : 'transparent')};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.text.secondary};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const ExportBtn = styled.button`
  font-size: 13px;
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-left: auto;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const Section = styled.section`
  & + & {
    margin-top: ${({ theme }) => theme.spacing(8)};
  }
`;

const SectionLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Row = styled(Card).attrs({ as: Link })`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    box-shadow: ${({ theme }) => theme.shadow.lg};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const Main = styled.div`
  min-width: 0;
`;

const RowText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const RowMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(0.5)};
`;

const RightActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-shrink: 0;
`;

const StatusSelect = styled.select`
  font-size: 12px;
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
`;

const StatusTag = styled.span<{ $tone: 'success' | 'danger' | 'neutral' }>`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  color: ${({ theme, $tone }) =>
    $tone === 'neutral' ? theme.colors.text.muted : theme.colors.status[$tone]};
  background: ${({ theme, $tone }) =>
    $tone === 'neutral' ? theme.colors.surfaceHover : `${theme.colors.status[$tone]}18`};
`;

const DeptTag = styled.span`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primarySoft};
`;

const EmptyHint = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/** Статусы поручения для фильтра реестра (пусто = все открытые: pending/in_progress/overdue). */
const STATUS_FILTER: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'открытые' },
  { value: 'pending', label: 'ожидает' },
  { value: 'in_progress', label: 'в работе' },
  { value: 'overdue', label: 'просрочено' },
  { value: 'done', label: 'выполнено' },
  { value: 'cancelled', label: 'отменено' },
];

const ASSIGNMENT_STATUS_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'pending', label: 'ожидает' },
  { value: 'in_progress', label: 'в работе' },
  { value: 'done', label: 'выполнено' },
  { value: 'overdue', label: 'просрочено' },
  { value: 'cancelled', label: 'отменено' },
];

/**
 * Накопительный реестр (N): сквозной по всем совещаниям свод действующих решений
 * на контроле + поручений, с фильтрами по отделу и статусу. Данные — дословно из
 * утверждённых протоколов (детерминированно, без LLM). Строки ведут на совещание.
 */
export function MeetingRegister() {
  const { can_read, can_write } = useAgentPermission('meetings');
  const { data: meData } = useMeQuery();
  const currentUser = meData?.user;
  const [searchParams, setSearchParams] = useSearchParams();
  const targetAssignmentId = searchParams.get('assignment') ?? '';
  const { activeProjectId, setActiveProjectId } = useActiveProjectContext();
  const [projectId, setProjectId] = useState(
    () => searchParams.get('project') ?? (targetAssignmentId ? '' : (activeProjectId ?? '')),
  );
  const [departmentId, setDepartmentId] = useState('');
  const [status, setStatus] = useState('');
  const [assignee, setAssignee] = useState('');
  const [onlyMine, setOnlyMine] = useState(false);

  useEffect(() => {
    if (!targetAssignmentId && activeProjectId && !projectId) {
      setProjectId(activeProjectId);
    }
  }, [activeProjectId, projectId, targetAssignmentId]);

  const { data: deptsData } = useListMeetingDepartmentsQuery();
  const { data: projectsData } = useListPtoProjectsQuery();
  const projects = projectsData?.items ?? [];
  const filters = {
    department_id: departmentId || undefined,
    status: status || undefined,
    assignee_id: onlyMine && currentUser?.id ? currentUser.id : undefined,
  };
  const { data, isFetching } = useGetMeetingsRegisterQuery(filters);
  const [exportDocx, { isFetching: exporting }] = useLazyExportMeetingsRegisterDocxQuery();
  const [setAssignmentStatus, { isLoading: savingStatus }] =
    useSetMeetingAssignmentStatusMutation();

  const decisions = data?.decisions ?? [];
  const allAssignments = data?.assignments ?? [];
  const depts = deptsData?.departments ?? [];

  const isMyAssignment = (a: RegisterAssignment): boolean => {
    if (!currentUser) return false;
    if (a.assignee_id && a.assignee_id === currentUser.id) return true;
    const myNorm = normName(currentUser.full_name);
    return Boolean(myNorm && normName(a.assignee_name) === myNorm);
  };

  // Кандидаты для фильтра «исполнитель» — из всех поручений текущего свода
  // (включая тех, у кого сматчилась карточка сотрудника или указано только ФИО).
  const assigneeOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const a of allAssignments) {
      const key = a.assignee_id ?? a.assignee_employee_id ?? a.assignee_name;
      if (key) {
        seen.set(key, a.assignee_name ?? key);
      }
    }
    return [...seen.entries()].sort((x, y) => x[1].localeCompare(y[1], 'ru'));
  }, [allAssignments]);

  // Фильтр по объекту, ссылке ?assignment= и исполнителю — на стороне клиента.
  const assignments = useMemo(() => {
    return allAssignments.filter((a) => {
      if (targetAssignmentId && a.id !== targetAssignmentId) return false;
      if (projectId && a.project_id && a.project_id !== projectId) return false;
      if (
        assignee &&
        (a.assignee_id ?? a.assignee_employee_id ?? a.assignee_name) !== assignee
      ) {
        return false;
      }
      return true;
    });
  }, [allAssignments, targetAssignmentId, projectId, assignee]);

  async function downloadDocx() {
    try {
      // Передаём assignee_id на сервер только если выбранный фильтр — валидный UUID учётки.
      const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        assignee,
      );
      const effectiveAssigneeId =
        onlyMine && currentUser?.id ? currentUser.id : isUuid ? assignee : undefined;
      triggerDownload(
        await exportDocx({
          ...filters,
          assignee_id: effectiveAssigneeId,
        }).unwrap(),
        'meetings-register.docx',
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('exportMeetingsRegisterDocx failed', err);
    }
  }

  if (!can_read) {
    return (
      <FallbackBanner tone="danger" title="Доступ запрещён">
        У вас нет прав на раздел «Совещания».
      </FallbackBanner>
    );
  }

  return (
    <div>
      <Intro>
        Действующие решения на контроле и поручения из всех утверждённых протоколов — сквозной
        накопительный свод. Фильтруйте по объекту, отделу и статусу; для своих поручений можно
        сменить статус прямо в реестре.
      </Intro>

      <Filters>
        <FilterLabel>Объект:</FilterLabel>
        <FilterSelect
          value={projectId}
          onChange={(e) => {
            const v = e.target.value;
            setProjectId(v);
            setActiveProjectId(v || null);
          }}
          aria-label="Фильтр по объекту"
        >
          <option value="">— все объекты —</option>
          {projects.map((p) => (
            <option value={p.id} key={p.id}>
              {p.name}
            </option>
          ))}
        </FilterSelect>
        <FilterLabel>Отдел:</FilterLabel>
        <FilterSelect
          value={departmentId}
          onChange={(e) => setDepartmentId(e.target.value)}
          aria-label="Фильтр по отделу"
        >
          <option value="">— все отделы —</option>
          {depts.map((d) => (
            <option value={d.id} key={d.id}>
              {d.name}
            </option>
          ))}
        </FilterSelect>
        <FilterLabel>Статус поручений:</FilterLabel>
        <FilterSelect
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label="Фильтр по статусу поручений"
        >
          {STATUS_FILTER.map((o) => (
            <option value={o.value} key={o.value || 'open'}>
              {o.label}
            </option>
          ))}
        </FilterSelect>
        <FilterLabel>Исполнитель:</FilterLabel>
        <FilterSelect
          value={assignee}
          onChange={(e) => {
            setAssignee(e.target.value);
            if (e.target.value) setOnlyMine(false);
          }}
          aria-label="Фильтр по исполнителю"
        >
          <option value="">— все —</option>
          {assigneeOptions.map(([id, name]) => (
            <option value={id} key={id}>
              {name}
            </option>
          ))}
        </FilterSelect>
        {currentUser && (
          <QuickToggleBtn
            type="button"
            $active={onlyMine}
            onClick={() => {
              const next = !onlyMine;
              setOnlyMine(next);
              if (next) setAssignee('');
            }}
          >
            Мои поручения
          </QuickToggleBtn>
        )}
        {targetAssignmentId && (
          <QuickToggleBtn
            type="button"
            $active
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('assignment');
              setSearchParams(next, { replace: true });
            }}
          >
            Сбросить фильтр поручения
          </QuickToggleBtn>
        )}
        <ExportBtn
          type="button"
          onClick={() => void downloadDocx()}
          disabled={exporting}
        >
          Скачать DOCX
        </ExportBtn>
      </Filters>

      <Section>
        <SectionLabel>Действующие решения на контроле</SectionLabel>
        {decisions.length > 0 ? (
          <List>
            {decisions.map((d) => (
              <Row to={`/agents/meetings/meetings/${d.meeting_id}`} key={d.item_id}>
                <Main>
                  <RowText>
                    {d.timecode ? `[${d.timecode}] ` : ''}
                    {d.text}
                  </RowText>
                  <RowMeta>
                    {d.meeting_title}
                    {d.scheduled_at ? ` · ${fmtMeetingDate(d.scheduled_at)}` : ''}
                  </RowMeta>
                </Main>
                {d.department_name && <DeptTag>{d.department_name}</DeptTag>}
              </Row>
            ))}
          </List>
        ) : (
          <EmptyHint>
            {isFetching ? 'Загружаю…' : 'Действующих решений по выбранному фильтру нет.'}
          </EmptyHint>
        )}
      </Section>

      <Section>
        <SectionLabel>Поручения</SectionLabel>
        {assignments.length > 0 ? (
          <List>
            {assignments.map((a) => {
              const canEditStatus = can_write || isMyAssignment(a);
              return (
                <Row to={`/agents/meetings/meetings/${a.meeting_id}`} key={a.id}>
                  <Main>
                    <RowText>{a.text}</RowText>
                    <RowMeta>
                      {a.meeting_title} · Ответственный: {a.assignee_name ?? 'уточнить'} · Срок:{' '}
                      {a.due_date ? fmtDueDate(a.due_date) : (a.due_text ?? 'уточнить')}
                      {a.department_name ? ` · ${a.department_name}` : ''}
                      {targetAgentLabel(a.target_agent)
                        ? ` · контур: ${targetAgentLabel(a.target_agent)}`
                        : ''}
                    </RowMeta>
                  </Main>
                  <RightActions
                    onClick={(e) => {
                      if (canEditStatus) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                  >
                    <StatusTag $tone={assignmentStatusTone(a.status)}>
                      {assignmentStatusLabel(a.status)}
                    </StatusTag>
                    {canEditStatus && (
                      <StatusSelect
                        value={a.status}
                        disabled={savingStatus}
                        aria-label="Сменить статус поручения"
                        onChange={(e) =>
                          void setAssignmentStatus({
                            id: a.id,
                            status: e.target.value,
                            meetingId: a.meeting_id,
                          })
                        }
                      >
                        {ASSIGNMENT_STATUS_OPTIONS.map((o) => (
                          <option value={o.value} key={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </StatusSelect>
                    )}
                  </RightActions>
                </Row>
              );
            })}
          </List>
        ) : (
          <EmptyHint>
            {isFetching ? 'Загружаю…' : 'Поручений по выбранному фильтру нет.'}
          </EmptyHint>
        )}
      </Section>
    </div>
  );
}

